import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import GoogleAuthSuccess from "./components/GoogleAuthSuccess";
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import EnterprisePage from "./pages/EnterprisePage";
import SolutionPage from "./pages/solutions/SolutionPage";
import ProductPage from "./pages/products/ProductPage";
import LoginPage from "./pages/LoginPage";
import ClientLoginPage from "./pages/ClientLoginPage";
import ClientInviteAcceptPage from "./pages/ClientInviteAcceptPage";
import SignupPage from "./pages/SignupPage";
import SignupReviewPage from "./pages/SignupReviewPage";
import SignupSuccessPage from "./pages/SignupSuccessPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import AdminDashboard from "./pages/AdminDashboard";
import WorkspaceDashboard from "./pages/WorkspaceDashboard";
import SubAccountsPage from "./pages/SubAccountsPage";
import BillingPage from "./pages/BillingPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import OnboardingBuilderPage from "./pages/OnboardingBuilderPage";
import PublicOnboardingFormPage from "./pages/PublicOnboardingFormPage";
import OnboardingPreviewPage from "./pages/OnboardingPreviewPage";
import BrandProfilesPage, { BrandingRedirect } from "./pages/subaccount/BrandProfilesPage";
import ProjectManagerPage from "./pages/subaccount/ProjectManagerPage";
import TrainingPage from "./pages/TrainingPage";
import AffiliatesPage from "./pages/AffiliatesPage";
import LeadsPage from "./pages/LeadsPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import SetPasswordPage from "./pages/SetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BillingSetupPage from "./pages/BillingSetupPage";
import BetaOnboardingPage from "./pages/BetaOnboardingPage";
import TrialSignupPage from "./pages/TrialSignupPage";
import TrialOnboardingPage from "./pages/TrialOnboardingPage";
import TrialGoogleCallback from "./pages/TrialGoogleCallback";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import TrustSecurityPage from "./pages/TrustSecurityPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import HelpCategoryPage from "./pages/HelpCategoryPage";
import HelpArticlePage from "./pages/HelpArticlePage";
import SupportPage from "./pages/SupportPage";
import StatusPage from "./pages/StatusPage";
import SecurityPage from "./pages/SecurityPage";
import ChangelogPage from "./pages/ChangelogPage";
import PublicReplayPage from "./pages/PublicReplayPage";
import PublicProposalPage from "./pages/PublicProposalPage";
import GHLLaunchPage from "./pages/GHLLaunchPage";
import ChallengeLanderPage from "./pages/funnel/ChallengeLanderPage";
import ChallengeKitPage from "./pages/funnel/ChallengeKitPage";
import ChallengeThanksPage from "./pages/funnel/ChallengeThanksPage";
import ChallengeVipPage from "./pages/funnel/ChallengeVipPage";
import FunnelAdminPage from "./pages/funnel/FunnelAdminPage";
import FunnelEditorPage from "./pages/funnel/editor/FunnelEditorPage";
import FunnelEditorPreviewPage from "./pages/funnel/editor/EditorPreviewPage";
import WelcomeBetaPopup from "./components/WelcomeBetaPopup";
import BetaEndedModal from "./components/BetaEndedModal";
import { BetaEndProvider } from "./context/BetaEndContext";
import QuickStartChecklist from "./components/QuickStartChecklist";
import FeedbackButton from "./components/FeedbackButton";
import LiveNotificationWidget from "./components/LiveNotificationWidget";

// Sub-Account Pages
import SubAccountDashboard from "./pages/subaccount/SubAccountDashboard";
import AIAgentsPage from "./pages/subaccount/AIAgentsPage";
import AgentEditorPage from "./pages/subaccount/AgentEditorPage";
import KnowledgeBasePage from "./pages/subaccount/KnowledgeBasePage";
import FunctionsPage from "./pages/subaccount/FunctionsPage";
import PhoneNumbersPage from "./pages/subaccount/PhoneNumbersPage";
import CallHistoryPage from "./pages/subaccount/CallHistoryPage";
import CallRoutingPage from "./pages/subaccount/CallRoutingPage";
import CallFlowCanvasPage from "./pages/subaccount/CallFlowCanvasPage";
import ScheduledCallsPage from "./pages/subaccount/ScheduledCallsPage";
import SubAccountBillingPage from "./pages/subaccount/SubAccountBillingPage";
import SubAccountSettingsPage from "./pages/subaccount/SubAccountSettingsPage";
import ContactTimelinePage from "./pages/subaccount/ContactsPage";
import ConversationsPage from "./pages/subaccount/ConversationsPage";
import WidgetsPage from "./pages/subaccount/WidgetsPage";

import { Toaster } from "./components/ui/sonner";
import UpdateBanner from "./components/UpdateBanner";
import { isPlatformPath } from "./utils/platformPaths";

// Redirect standalone billing to Settings > Billing tab
const BillingRedirect = () => {
  const loc = useLocation();
  const settingsPath = loc.pathname.replace('/billing', '/settings') + '?tab=billing';
  return <Navigate to={settingsPath} replace />;
};

// Placeholder pages for routes not yet built
const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen bg-space-900 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-display font-semibold text-white mb-2">{title}</h1>
      <p className="text-gray-400">Coming soon...</p>
    </div>
  </div>
);

// Router wrapper to handle OAuth callback
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.pathname === '/free-trial/google') {
    return <TrialGoogleCallback />;
  }
  if (location.pathname === '/signup/google') {
    return <SignupPage />;
  }
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/solutions/:slug" element={<SolutionPage />} />
      <Route path="/products/:slug" element={<ProductPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/client-login/:subAccountId" element={<ClientLoginPage />} />
      <Route path="/l/:subAccountId" element={<ClientLoginPage />} />
      <Route path="/client-invite" element={<ClientInviteAcceptPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup-review" element={<SignupReviewPage />} />
      <Route path="/signup/success" element={<SignupSuccessPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/beta-onboarding" element={<BetaOnboardingPage />} />
      <Route path="/join-beta" element={<Navigate to="/signup" replace />} />
      <Route path="/free-trial" element={<TrialSignupPage />} />
      <Route path="/trial-onboarding" element={<TrialOnboardingPage />} />
      {/* GHL Custom Menu Link landing route — resolves the matching
          Voxinity sub-account from a GHL location_id and redirects. */}
      <Route path="/ghl/launch" element={<GHLLaunchPage />} />

      {/* Challenge Funnel (public) */}
      <Route path="/challenge" element={<ChallengeLanderPage />} />
      <Route path="/challenge/kit" element={<ChallengeKitPage />} />
      <Route path="/challenge/thanks" element={<ChallengeThanksPage />} />
      <Route path="/challenge/vip" element={<ChallengeVipPage />} />
      {/* Funnel editor live preview (iframe target for /admin/funnel/editor) */}
      <Route path="/funnel-editor-preview" element={<FunnelEditorPreviewPage />} />
      {/* Public client onboarding form (embeddable) */}
      <Route path="/onboard/:slug" element={<PublicOnboardingFormPage />} />
      <Route path="/onboard/:slug/preview" element={<OnboardingPreviewPage />} />
      {/* Public legal + help routes — required for marketplace approval */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/trust" element={<TrustSecurityPage />} />
      <Route path="/security" element={<TrustSecurityPage />} />
      <Route path="/terms-of-service" element={<TermsPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/help/category/:categoryId" element={<HelpCategoryPage />} />
      <Route path="/help/:slug" element={<HelpArticlePage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/replay/:token" element={<PublicReplayPage />} />
      <Route path="/p/:token" element={<PublicProposalPage />} />
      <Route path="/billing-setup" element={
        <ProtectedRoute>
          <BillingSetupPage />
        </ProtectedRoute>
      } />
      <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy dashboard redirect */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        } 
      />
      
      {/* Workspace routes */}
      <Route 
        path="/workspace/:workspaceId" 
        element={
          <ProtectedRoute>
            <WorkspaceDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-accounts" 
        element={
          <ProtectedRoute>
            <SubAccountsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/leads" 
        element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/phone-numbers" 
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Phone Numbers" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/integrations" 
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingBuilderPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/branding" 
        element={
          <ProtectedRoute>
            <BrandingRedirect />
          </ProtectedRoute>
        } 
      />
      <Route path="/workspace/:workspaceId/sub-account/:subAccountId/brand-profiles" element={<ProtectedRoute><BrandProfilesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/sub-account/:subAccountId/brand-profiles/:profileId" element={<ProtectedRoute><BrandProfilesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/sub-account/:subAccountId/project-manager/*" element={<ProtectedRoute><ProjectManagerPage /></ProtectedRoute>} />
      <Route 
        path="/workspace/:workspaceId/usage" 
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Usage Reports" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/billing" 
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/training" 
        element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/affiliates" 
        element={
          <ProtectedRoute>
            <AffiliatesPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Settings */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Sub-Account Routes */}
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId" 
        element={
          <ProtectedRoute>
            <SubAccountDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/agents" 
        element={
          <ProtectedRoute>
            <AIAgentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/agents/:agentId" 
        element={
          <ProtectedRoute>
            <AgentEditorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/knowledge" 
        element={
          <ProtectedRoute>
            <KnowledgeBasePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/functions" 
        element={
          <ProtectedRoute>
            <FunctionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/phone-numbers" 
        element={
          <ProtectedRoute>
            <PhoneNumbersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/call-routing" 
        element={
          <ProtectedRoute>
            <CallRoutingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/call-routing/:flowId/canvas" 
        element={
          <ProtectedRoute>
            <CallFlowCanvasPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/call-history" 
        element={
          <ProtectedRoute>
            <CallHistoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/contacts" 
        element={
          <ProtectedRoute>
            <ContactTimelinePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/conversations" 
        element={
          <ProtectedRoute>
            <ConversationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/scheduled-calls" 
        element={
          <ProtectedRoute>
            <ScheduledCallsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/billing" 
        element={
          <ProtectedRoute>
            <BillingRedirect />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/settings" 
        element={
          <ProtectedRoute>
            <SubAccountSettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workspace/:workspaceId/sub-account/:subAccountId/widgets" 
        element={
          <ProtectedRoute>
            <WidgetsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/funnel" 
        element={
          <ProtectedRoute requireAdmin>
            <FunnelAdminPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/funnel/editor" 
        element={
          <ProtectedRoute requireAdmin>
            <FunnelEditorPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

// Redirect from /dashboard to the user's workspace (or onboarding if not completed)
function DashboardRedirect() {
  const { user } = useAuth();
  
  // Redirect to onboarding if not completed
  if (user && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  const currentWorkspaceId = localStorage.getItem('currentWorkspaceId');
  
  if (currentWorkspaceId) {
    return <Navigate to={`/workspace/${currentWorkspaceId}`} replace />;
  }
  
  // If no workspace is saved, show old dashboard which will fetch and redirect
  return <DashboardPage />;
}

// Keeps public marketing/funnel/legal pages rendering EXACTLY as designed,
// regardless of the logged-in user's platform dark/light preference. The
// 'force-dark' class gates every html.light override in index.css.
function ThemeIsolation() {
  const location = useLocation();
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('trial-viewport', /^\/(free-trial|trial-onboarding)(\/|$)/.test(location.pathname));
    if (isPlatformPath(location.pathname)) {
      root.classList.remove('force-dark');
    } else {
      root.classList.add('force-dark');
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <ThemeIsolation />
          <UpdateBanner />
          <AuthProvider>
            <BetaEndProvider>
              <AppRouter />
              <WelcomeBetaPopup />
              <BetaEndedModal />
              <FeedbackButton />
              <LiveNotificationWidget />
            </BetaEndProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
